import {
  createAsyncCheckout,
  CreateTestCheckoutResponse,
} from "@/api/rest/checkoutApi";
import { CheckoutIntent } from "./types/CheckoutIntent";
import { assign, createMachine, fromPromise } from "xstate";

// Define navigate as a simple function type instead of using React Router's type
type NavigateFunction = (path: string) => void;
interface CheckoutStep {
  visible: boolean;
  open: boolean;
  forceComplete: boolean;
  canOpen: boolean;
}

export interface CheckoutContext {
  userId: string | null;
  checkoutIntent: CheckoutIntent | null;

  checkoutResponse: CreateTestCheckoutResponse | null;

  // Input state
  shippingInput?: {
    firstName: string;
    lastName: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    phoneNumber?: string;
    email?: string;
  };

  // Checkout steps
  signIn: CheckoutStep;
  shipping: CheckoutStep;
  payment: CheckoutStep;
  retailerLogin: CheckoutStep;

  // injected
  testOrder: boolean;
  navigate?: NavigateFunction;
}

export const checkoutUIMachine = createMachine(
  {
    id: "checkoutUI",
    initial: "initial",
    context: ({
      input,
    }: {
      input?: { context: Partial<CheckoutContext> };
    }): CheckoutContext => {
      return {
        userId: null,
        checkoutIntent: null,
        signIn: {
          visible: true,
          open: true,
          canOpen: false,
          forceComplete: true,
        },
        shipping: {
          visible: true,
          open: false,
          canOpen: false,
          forceComplete: true,
        },
        payment: {
          visible: true,
          open: false,
          canOpen: false,
          forceComplete: true,
        },
        retailerLogin: {
          visible: false,
          open: false,
          canOpen: false,
          forceComplete: true,
        },
        ...(input?.context || {}),
      } as CheckoutContext;
    },
    on: {
      SET_USER_ID: [
        // If we have everything needed for payment intent, go create it
        {
          actions: assign({
            userId: ({ event }) => {
              return event.data.userId;
            },
            signIn: {
              visible: false,
              open: false,
              forceComplete: false,
              canOpen: false,
            },
            retailerLogin: ({ context }) => ({
              ...context.retailerLogin,
              visible: true,
            }),
          }),
          target: ".editing",
        },
      ],
      SET_SHIPPING: {
        actions: assign({
          shippingInput: ({ event }) => event.data,
          shipping: ({ event, context }) => {
            return {
              ...context.shipping,
              forceComplete: true,
              open: true,
            };
          },
        }),
        target: ".editing",
      },
      SET_CHECKOUT_INTENT: [
        {
          actions: [
            assign({
              checkoutIntent: ({ event }) => event.data.checkoutIntent,
              shippingInput: ({ event }) => {
                const checkoutIntent = event.data
                  .checkoutIntent as CheckoutIntent;
                if (checkoutIntent.shippingAddress) {
                  return {
                    firstName: checkoutIntent.shippingAddress.firstName,
                    lastName: checkoutIntent.shippingAddress.lastName,
                    address1: checkoutIntent.shippingAddress.address1,
                    address2: checkoutIntent.shippingAddress.address2,
                    city: checkoutIntent.shippingAddress.city,
                    state: checkoutIntent.shippingAddress.state,
                    zipCode: checkoutIntent.shippingAddress.zipCode,
                    country: checkoutIntent.shippingAddress.country,
                    phoneNumber: checkoutIntent.shippingAddress.phoneNumber,
                    email: checkoutIntent.shippingAddress.email,
                  };
                }
                return undefined;
              },
              payment: ({ event, context }) => {
                const hasPaymentMethod =
                  !!event.data.checkoutIntent?.stripePaymentMethod;
                return {
                  ...context.payment,
                  forceComplete: !hasPaymentMethod,
                  open: !hasPaymentMethod,
                  canOpen: hasPaymentMethod,
                };
              },
              shipping: ({ event, context }) => {
                const hasShippingInput =
                  !!event.data.checkoutIntent?.shippingAddress;
                const hasPaymentMethod =
                  !!event.data.checkoutIntent?.stripePaymentMethod;
                return {
                  ...context.shipping,
                  forceComplete: !hasShippingInput,
                  canOpen: hasShippingInput,
                  open: !hasShippingInput && hasPaymentMethod,
                };
              },
              retailerLogin: ({ event, context }) => {
                const hasRetailerConnection =
                  !!event.data.checkoutIntent?.retailerConnection;
                const hasShippingAddress =
                  !!event.data.checkoutIntent?.shippingAddress;
                const hasPaymentMethod =
                  !!event.data.checkoutIntent?.stripePaymentMethod;
                return {
                  ...context.retailerLogin,
                  forceComplete: !hasRetailerConnection,
                  open:
                    !hasRetailerConnection &&
                    hasShippingAddress &&
                    hasPaymentMethod,
                  canOpen:
                    hasRetailerConnection &&
                    hasShippingAddress &&
                    hasPaymentMethod,
                };
              },
            }),
          ],
          target: ".editing",
        },
      ],
      CHANGE_RETAILER_LOGIN_COMPLETE_STATUS: {
        actions: assign(({ event, context }) => ({
          retailerLogin: {
            ...context.retailerLogin,
            forceComplete: event.data.forceComplete,
          },
        })),
        target: ".editing",
      },
    },
    states: {
      initial: {
        on: {
          SET_CHECKOUT_INTENT: {
            target: "editing",
            guard: "hasCheckoutIntent",
          },
        },
      },
      editing: {
        always: {
          target: "ready_to_checkout",
          guard: ({ context }) => {
            const ctx = context as CheckoutContext;
            const hasCompletedAllMandatorySteps = [
              ctx.signIn.forceComplete,
              ctx.shipping.forceComplete,
              ctx.payment.forceComplete,
              ctx.retailerLogin.forceComplete,
            ].every((step) => !step);
            return hasCompletedAllMandatorySteps;
          },
        },
        on: {
          SET_IS_VALID_ADDRESS: {
            actions: [
              assign({
                shipping: ({ event, context }) => ({
                  ...context.shipping,
                  forceComplete: !event.data,
                  open: !event.data,
                }),
              }),
            ],
          },
          TOGGLE: {
            actions: [
              assign(({ event, context }) => {
                const section = event.data.section as
                  | "shipping"
                  | "payment"
                  | "retailerLogin";
                return {
                  [section]: {
                    ...context[section],
                    open: context[section].forceComplete
                      ? true
                      : !context[section].open,
                  },
                };
              }),
            ],
          },
        },
      },
      ready_to_checkout: {
        on: {
          SUBMIT_CHECKOUT: {
            target: "checking_out",
          },
          TOGGLE: {
            actions: [
              assign(({ event, context }) => {
                const section = event.data.section as
                  | "shipping"
                  | "payment"
                  | "retailerLogin";
                return {
                  [section]: {
                    ...context[section],
                    open: context[section].forceComplete
                      ? true
                      : !context[section].open,
                  },
                };
              }),
            ],
            target: "editing",
          },
        },
      },
      checking_out: {
        invoke: {
          src: "createCheckout",
          input: ({ context }) => ({ context }),
          onDone: [
            {
              target: "ready_to_checkout",
              guard: ({ event }) => event.output.status === "error",
              actions: assign({
                checkoutResponse: ({ event }) => event.output,
              }),
            },
            {
              target: "success",
              actions: assign({
                checkoutResponse: ({ event }) => event.output,
              }),
            },
          ],
          onError: {
            target: "ready_to_checkout",
          },
        },
      },
      error: {},
      success: {
        type: "final",
        entry: ["navigateToOrderConfirmation"],
        on: {
          RESET: "initial",
        },
      },
    },
  },
  {
    actors: {
      createCheckout: fromPromise(
        async ({ input }: { input: { context: CheckoutContext } }) => {
          try {
            if (!input.context.checkoutIntent) {
              throw new Error("Checkout intent is missing from context");
            }

            const data = await createAsyncCheckout({
              checkoutIntentId: input.context.checkoutIntent.checkoutIntentId,
              isTestOrder: input.context.testOrder,
            });
            return data;
          } catch (error) {
            console.error("Error creating checkout", error);
            throw error;
          }
        }
      ),
    },
    actions: {
      navigateToOrderConfirmation: ({
        context,
      }: {
        context: CheckoutContext;
      }) => {
        if (context.navigate) {
          context.navigate(
            `/checkoutSummary?orderId=${context.checkoutResponse?.userRetailerCheckoutOrder?.orderId}`
          );
        }
      },
      openNextSection: ({ context }) => {
        const sections = [
          context.payment,
          context.shipping,
          context.retailerLogin,
        ];

        const nextSectionIndex = sections.findIndex(
          (section) => section.forceComplete
        );
        const nextSection = sections[nextSectionIndex];

        if (nextSection) {
          nextSection.open = true;
          nextSection.visible = true;
          nextSection.canOpen = true;
        }

        // Mark the items before nextSectionIndex as canOpen = true and forceComplete = false
        sections.slice(0, nextSectionIndex).forEach((section) => {
          section.canOpen = true;
          section.forceComplete = false;
          section.open = false;
        });
      },
    },
    guards: {
      hasCheckoutIntent: ({ context }) => {
        return (context as unknown as CheckoutContext).checkoutIntent !== null;
      },
      hasStepsToComplete: ({ context }) => {
        const hasStepsToComplete = [
          (context as unknown as CheckoutContext).signIn.forceComplete,
          (context as unknown as CheckoutContext).shipping.forceComplete,
          (context as unknown as CheckoutContext).payment.forceComplete,
          (context as unknown as CheckoutContext).retailerLogin.forceComplete,
        ].some((step) => step);

        return hasStepsToComplete;
      },
      hasCompletedAllMandatorySteps: ({ context }) => {
        const hasCompletedAllMandatorySteps = [
          (context as unknown as CheckoutContext).signIn.forceComplete,
          (context as unknown as CheckoutContext).shipping.forceComplete,
          (context as unknown as CheckoutContext).payment.forceComplete,
          (context as unknown as CheckoutContext).retailerLogin.forceComplete,
        ].every((step) => !step);
        return hasCompletedAllMandatorySteps;
      },
    },
  }
);
